import React, { memo, useRef } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'react-bootstrap';
import { useAppContext } from '../../../providers/AppProvider';

echarts.use([
  BarChart,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  CanvasRenderer
]);

const getChartOptions = (shiftName, shiftData, theme, getThemeColor) => {
  const machines = Object.keys(shiftData);
  // const rawDiameters = Array.from(
  //   new Set(machines.flatMap(machine => Object.keys(shiftData[machine])))
  // ).sort((a, b) => parseFloat(a) - parseFloat(b)); // Sort diameters numerically

  const rawDiameters = Array.from(
    new Set(
      machines
        .map(machine => Object.keys(shiftData[machine])) // Map each machine to its keys (arrays of diameters)
        .reduce((acc, val) => acc.concat(val), []) // Flatten the resulting arrays
    )
  ).sort((a, b) => parseFloat(a) - parseFloat(b)); // Sort diameters numerically

  // Format diameters to `toFixed(2)`
  const diameters = rawDiameters.map(diameter =>
    parseFloat(diameter).toFixed(2)
  );

  // Prepare series
  const series = machines.map(machine => ({
    name: machine,
    type: 'bar',
    stack: 'total', // Stack bars for better comparison
    data: rawDiameters.map(diameter => shiftData[machine][diameter] || 0), // Use raw diameters as keys
    label: {
      show: true,
      position: 'top',
      formatter: ({ value }) => (value > 0 ? value.toFixed(2) : ''), // Show value only if > 0
      fontSize: 10,
      color: getThemeColor('gray-800')
    },
    barMaxWidth: 14, // se ingusteaza singur cand sunt multe diametre
    itemStyle: {
      color: getThemeColor(machine) // Use a dynamic color for each machine
    }
  }));

  return {
    title: {
      text: `Shift ${shiftName}`,
      left: 'center',
      textStyle: {
        fontSize: 16,
        color: getThemeColor('gray-800')
      }
    },
    tooltip: {
      trigger: 'item',
      formatter: ({ seriesName, value, name }) =>
        `<strong>Diameter: ${name}</strong><br>${seriesName}: ${value.toFixed(
          2
        )}`
    },
    legend: {
      data: machines,
      bottom: 0,
      textStyle: {
        fontSize: 12,
        color: getThemeColor('gray-800')
      }
    },
    xAxis: {
      type: 'category',
      data: diameters, // Use formatted diameters for X-axis labels
      axisLabel: {
        fontSize: 12,
        color: getThemeColor('gray-800')
      },
      axisLine: {
        lineStyle: {
          color: getThemeColor('gray-300')
        }
      },
      axisTick: {
        alignWithLabel: true
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 12,
        formatter: '{value} km',
        color: getThemeColor('gray-800')
      },
      axisLine: {
        lineStyle: { color: getThemeColor('gray-300') }
      },
      splitLine: {
        show: false,
        lineStyle: { color: getThemeColor('gray-300') }
      }
    },
    series,
    grid: {
      left: '10%',
      right: '10%',
      bottom: '15%',
      top: '15%',
      containLabel: true
    },
    animation: true
  };
};

const DynamicShiftBarChart = ({ data }) => {
  const chartRef = useRef(null);

  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  return Object.entries(data).map(([shiftName, shiftData]) => (
    <Card key={shiftName} className="mb-3">
      <Card.Body>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getChartOptions(shiftName, shiftData, theme, getThemeColor)}
          style={{ height: '400px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  ));
};

export default memo(DynamicShiftBarChart);
